/**
 * LayoutPicker.jsx
 * 
 * Right panel for selecting page layouts.
 * Shows visual previews of each layout option.
 */

import React from 'react';
import { useEditor } from './EditorContext';
import {
  Square,
  Columns,
  Grid3X3,
  Grid2X2,
  AlignLeft,
  AlignRight,
  FileText,
  Maximize,
  LayoutGrid
} from 'lucide-react';

const LAYOUTS = [
  {
    id: 'hero',
    name: '1 Photo',
    description: 'Single photo, full page',
    icon: Square,
  },
  {
    id: 'hero-text',
    name: '1 Photo + Caption',
    description: 'Photo with text below',
    icon: FileText,
  },
  {
    id: 'grid-2',
    name: '2 Photos',
    description: 'Side by side',
    icon: Columns,
  },
  {
    id: 'grid-3',
    name: '3 Photos',
    description: '1 large + 2 small',
    icon: Grid3X3,
  },
  {
    id: 'grid-4',
    name: '4 Photos',
    description: '2x2 grid',
    icon: Grid2X2,
  },
  {
    id: 'text-left',
    name: 'Text Left',
    description: 'Story with photo',
    icon: AlignLeft,
  },
  {
    id: 'text-right',
    name: 'Text Right',
    description: 'Photo with story',
    icon: AlignRight,
  },
  {
    id: 'text-only',
    name: 'Text Only',
    description: 'Full text page',
    icon: FileText,
  },
  {
    id: 'full-bleed',
    name: 'Full Bleed',
    description: 'Edge to edge photo',
    icon: Maximize,
  },
];

export default function LayoutPicker({ page, isMobile = false }) {
  const { updatePageLayout, toggleLayoutPicker } = useEditor();
  
  if (!page) {
    return (
      <div className="p-4">
        <p className="text-gray-400 text-sm">Select a page to change its layout</p>
      </div>
    );
  }
  
  // Don't allow layout changes for locked pages
  if (page.isLocked) {
    return (
      <div className="p-4">
        <h3 className="font-medium text-gray-700 mb-2">Page Layout</h3>
        <p className="text-gray-400 text-sm">This page's layout cannot be changed</p>
      </div>
    );
  }
  
  const handleSelectLayout = (layoutId) => {
    updatePageLayout(page.id, layoutId);
    if (isMobile) {
      toggleLayoutPicker(false);
    }
  };
  
  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-gray-700">Page Layout</h3>
        {isMobile && (
          <button
            onClick={() => toggleLayoutPicker(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>
      
      {/* Current page info */}
      <div className="mb-4 p-3 bg-ocean-50 rounded-lg">
        <p className="text-sm text-ocean-800">
          <span className="font-medium">Page {page.sortOrder + 1}</span>
          {page.sectionTitle && (
            <span className="block text-ocean-600 truncate">
              {page.sectionTitle}
            </span>
          )}
        </p>
      </div>
      
      {/* Layout options */}
      <div className="grid grid-cols-2 gap-2">
        {LAYOUTS.map((layout) => {
          const Icon = layout.icon;
          const isSelected = page.layout === layout.id;
          
          return (
            <button
              key={layout.id}
              onClick={() => handleSelectLayout(layout.id)}
              className={`
                p-3 rounded-lg border-2 transition-all text-left
                ${isSelected 
                  ? 'border-ocean-500 bg-ocean-50' 
                  : 'border-gray-200 hover:border-ocean-300 bg-white'
                }
              `}
            >
              {/* Layout preview */}
              <div className="flex items-center justify-center mb-2">
                <LayoutPreview layoutId={layout.id} isSelected={isSelected} />
              </div>
              
              {/* Layout name */}
              <p className={`text-xs font-medium ${isSelected ? 'text-ocean-900' : 'text-gray-700'}`}>
                {layout.name}
              </p>
            </button>
          );
        })}
      </div>
      
      {/* Tips section */}
      <div className="mt-6 p-3 bg-gray-50 rounded-lg">
        <h4 className="text-xs font-medium text-gray-600 mb-1">Tips</h4>
        <ul className="text-xs text-gray-500 space-y-1">
          <li>• Hero layout works best for standout photos</li>
          <li>• Use text layouts for journal entries</li>
          <li>• Grid layouts show multiple moments together</li>
        </ul>
      </div>
    </div>
  );
}

// ============================================
// LAYOUT PREVIEW COMPONENT
// ============================================

function LayoutPreview({ layoutId, isSelected }) {
  const baseClass = isSelected ? 'bg-ocean-300' : 'bg-gray-300';
  const containerClass = 'w-12 h-12 rounded border border-gray-200 overflow-hidden flex';
  
  switch (layoutId) {
    case 'hero':
      return (
        <div className={containerClass}>
          <div className={`w-full h-full ${baseClass}`} />
        </div>
      );
      
    case 'hero-text':
      return (
        <div className={`${containerClass} flex-col`}>
          <div className={`w-full h-3/4 ${baseClass}`} />
          <div className="w-full h-1/4 bg-white p-0.5">
            <div className="w-full h-0.5 bg-gray-200 rounded mb-0.5" />
            <div className="w-2/3 h-0.5 bg-gray-200 rounded" />
          </div>
        </div>
      );
      
    case 'grid-2':
      return (
        <div className={`${containerClass} gap-0.5 p-0.5`}>
          <div className={`w-1/2 h-full ${baseClass} rounded-sm`} />
          <div className={`w-1/2 h-full ${baseClass} rounded-sm`} />
        </div>
      );
      
    case 'grid-3':
      return (
        <div className={`${containerClass} gap-0.5 p-0.5`}>
          <div className={`w-1/2 h-full ${baseClass} rounded-sm`} />
          <div className="w-1/2 h-full flex flex-col gap-0.5">
            <div className={`w-full h-1/2 ${baseClass} rounded-sm`} />
            <div className={`w-full h-1/2 ${baseClass} rounded-sm`} />
          </div>
        </div>
      );
      
    case 'grid-4':
      return (
        <div className={`${containerClass} flex-wrap gap-0.5 p-0.5`}>
          <div className={`w-[calc(50%-1px)] h-[calc(50%-1px)] ${baseClass} rounded-sm`} />
          <div className={`w-[calc(50%-1px)] h-[calc(50%-1px)] ${baseClass} rounded-sm`} />
          <div className={`w-[calc(50%-1px)] h-[calc(50%-1px)] ${baseClass} rounded-sm`} />
          <div className={`w-[calc(50%-1px)] h-[calc(50%-1px)] ${baseClass} rounded-sm`} />
        </div>
      );
      
    case 'text-left':
      return (
        <div className={`${containerClass} gap-0.5`}>
          <div className="w-1/2 h-full bg-white p-1 flex flex-col gap-0.5">
            <div className="w-full h-0.5 bg-gray-200 rounded" />
            <div className="w-3/4 h-0.5 bg-gray-200 rounded" />
            <div className="w-full h-0.5 bg-gray-200 rounded" />
          </div>
          <div className={`w-1/2 h-full ${baseClass}`} />
        </div>
      );
      
    case 'text-right':
      return (
        <div className={`${containerClass} gap-0.5`}>
          <div className={`w-1/2 h-full ${baseClass}`} />
          <div className="w-1/2 h-full bg-white p-1 flex flex-col gap-0.5">
            <div className="w-full h-0.5 bg-gray-200 rounded" />
            <div className="w-3/4 h-0.5 bg-gray-200 rounded" />
            <div className="w-full h-0.5 bg-gray-200 rounded" />
          </div>
        </div>
      );
      
    case 'text-only':
      return (
        <div className={`${containerClass} bg-white p-2 flex-col gap-1`}>
          <div className="w-full h-1 bg-gray-200 rounded" />
          <div className="w-3/4 h-1 bg-gray-200 rounded" />
          <div className="w-full h-1 bg-gray-200 rounded" />
          <div className="w-2/3 h-1 bg-gray-200 rounded" />
        </div>
      );
      
    case 'full-bleed':
      return (
        <div className={`${containerClass} p-0`}>
          <div className={`w-full h-full ${baseClass}`} />
        </div>
      );
      
    default:
      return (
        <div className={containerClass}>
          <div className={`w-full h-full ${baseClass}`} />
        </div>
      );
  }
}
